import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import enMessages from './locales/en';

/**
 * Translations for the gift voucher shop.
 *
 * Same ten customer-facing locales and the same message-tree shape as the booking widget
 * (apps/widget/src/i18n), so a guest who books in Basque also buys a voucher in Basque.
 * Kept as its own small provider rather than shared with the widget because the two apps
 * ship separately and have no message keys in common.
 */

export type LocaleCode = 'en' | 'nl' | 'fr-FR' | 'fr-CA' | 'es' | 'pt' | 'de' | 'ca' | 'eu' | 'it';

export interface Messages {
  [key: string]: string | Messages;
}

const SUPPORTED: LocaleCode[] = ['en', 'nl', 'fr-FR', 'fr-CA', 'es', 'pt', 'de', 'ca', 'eu', 'it'];

const STORAGE_KEY = 'tenseat_gift_locale';

export function normalizeLocale(input?: string | null): LocaleCode {
  const value = (input || '').trim().toLowerCase();
  if (!value) return 'en';
  if (value === 'fr-ca') return 'fr-CA';
  if (value === 'fr' || value.startsWith('fr-')) return 'fr-FR';
  if (value === 'nl' || value.startsWith('nl-')) return 'nl';
  if (value === 'es' || value.startsWith('es-')) return 'es';
  if (value === 'pt' || value.startsWith('pt-')) return 'pt';
  if (value === 'de' || value.startsWith('de-')) return 'de';
  if (value === 'ca' || value.startsWith('ca-')) return 'ca';
  if (value === 'eu' || value.startsWith('eu-')) return 'eu';
  if (value === 'it' || value.startsWith('it-')) return 'it';
  return 'en';
}

/**
 * The tag to hand `Intl` for dates and money.
 *
 * Not every browser ships ICU data for every locale we translate into — Basque in
 * particular is missing from some builds — and `Intl` answers a missing locale with
 * American formatting, which reads far worse on a Basque page than its nearest neighbour
 * does. So each locale carries a fallback chain and we take the first tag the browser
 * actually knows.
 */
const INTL_FALLBACKS: Record<LocaleCode, string[]> = {
  en: ['en-GB'],
  nl: ['nl'],
  'fr-FR': ['fr-FR', 'fr'],
  'fr-CA': ['fr-CA', 'fr'],
  es: ['es'],
  pt: ['pt-PT', 'pt'],
  de: ['de'],
  ca: ['ca', 'es'],
  eu: ['eu', 'es'],
  it: ['it'],
};

const resolvedIntlLocales = new Map<LocaleCode, string>();

export function intlLocale(locale: LocaleCode): string {
  const cached = resolvedIntlLocales.get(locale);
  if (cached) return cached;

  const candidates = [...(INTL_FALLBACKS[locale] || []), 'en-GB'];
  let resolved = 'en-GB';
  try {
    resolved = candidates.find((tag) => Intl.DateTimeFormat.supportedLocalesOf([tag]).length > 0) || 'en-GB';
  } catch {
    /* an exotic tag throws rather than returning nothing */
  }
  resolvedIntlLocales.set(locale, resolved);
  return resolved;
}

async function loadMessages(locale: LocaleCode): Promise<Messages> {
  switch (locale) {
    case 'nl':
      return (await import('./locales/nl')).default;
    case 'fr-FR':
      return (await import('./locales/fr-FR')).default;
    case 'fr-CA':
      return (await import('./locales/fr-CA')).default;
    case 'es':
      return (await import('./locales/es')).default;
    case 'pt':
      return (await import('./locales/pt')).default;
    case 'de':
      return (await import('./locales/de')).default;
    case 'ca':
      return (await import('./locales/ca')).default;
    case 'eu':
      return (await import('./locales/eu')).default;
    case 'it':
      return (await import('./locales/it')).default;
    case 'en':
    default:
      return enMessages;
  }
}

/** `?lang=` beats a remembered choice, which beats the browser. */
function detectInitialLocale(): { locale: LocaleCode; explicit: boolean } {
  try {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang) return { locale: normalizeLocale(urlLang), explicit: true };
  } catch {
    /* no window search in a non-browser context */
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { locale: normalizeLocale(stored), explicit: true };
  } catch {
    /* storage can be blocked */
  }
  if (typeof navigator !== 'undefined') {
    return { locale: normalizeLocale(navigator.language), explicit: false };
  }
  return { locale: 'en', explicit: false };
}

function getNested(messages: Messages, key: string): string | undefined {
  let current: string | Messages | undefined = messages;
  for (const part of key.split('.')) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }
  return typeof current === 'string' ? current : undefined;
}

function formatTemplate(template: string, params?: Record<string, unknown>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(params[key] ?? ''));
}

export interface I18nContextValue {
  locale: LocaleCode;
  t: (key: string, params?: Record<string, unknown>) => string;
  setLocale: (locale: LocaleCode) => void;
  /**
   * The venue's own language, applied only when the guest has not asked for something
   * else — a Dutch restaurant's shop should open in Dutch, but a `?lang=fr` link or a
   * remembered choice still wins.
   */
  applyVenueLocale: (language?: string | null) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initial = useMemo(detectInitialLocale, []);
  const [locale, setLocaleState] = useState<LocaleCode>(initial.locale);
  const [messages, setMessages] = useState<Messages>(initial.locale === 'en' ? enMessages : {});
  const chosenByGuest = useRef(initial.explicit);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await loadMessages(locale);
        if (!cancelled) setMessages(loaded);
      } catch (error) {
        console.error('Failed to load gift voucher translations', { locale, error });
        if (!cancelled) setMessages(enMessages);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: LocaleCode) => {
    const resolved = SUPPORTED.includes(next) ? next : 'en';
    chosenByGuest.current = true;
    try {
      localStorage.setItem(STORAGE_KEY, resolved);
    } catch {
      /* storage can be blocked */
    }
    setLocaleState(resolved);
  }, []);

  const applyVenueLocale = useCallback((language?: string | null) => {
    if (chosenByGuest.current || !language) return;
    setLocaleState(normalizeLocale(language));
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, unknown>) =>
      formatTemplate(getNested(messages, key) ?? getNested(enMessages, key) ?? key, params),
    [messages]
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, t, setLocale, applyVenueLocale }),
    [locale, t, setLocale, applyVenueLocale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
